<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="Order Item Value Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="$emit('dateApplied')"
            @reset="$emit('dateReset')">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingStartDate"
                :endDate="editingEndDate"
                @updateStartDate="editingStartDate = $event; $emit('updateEditingStartDate', $event)"
                @updateEndDate="editingEndDate = $event; $emit('updateEditingEndDate', $event)"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
              </div>                
            </template>
          </ReportFilterPopover>
        </div>
        <StaticDataTable class="static-table-list" v-if="hasChartData" :smallRows="true">
          <template v-slot:header>
            <tr>
              <th width="60%">{{orderValueTableData.nameTitle}}</th>
              <th width="40%">{{orderValueTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of orderValueTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="orderValueSeries"
          :legend="orderValueLegend"
          :options="orderValueOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'
import { UOMTypeUnitsLookup } from '@/store/types'

const OrderItemValueChartTypes = {
  orderItemValueContact: 'orderItemValueContact',
  orderItemUnitsContact: 'orderItemUnitsContact'
}

export default {
  name: 'OrderItemValueChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    stockId: Number,
    contactId: Number,
    uomType: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    startDate: String,
    endDate: String
  },
  OrderItemValueChartTypes: OrderItemValueChartTypes,
  data() {
    return {
      orderValueSeries: [],
      orderValueTableData: {},
      orderValueLegend: [],
      orderValueOptions: {},

      editingStartDate: this.startDate,
      editingEndDate: this.endDate
    }
  },
  computed: {
    hasChartData() {
      if(this.orderValueSeries) {
        return this.orderValueSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    },
    hasContactFilter() {
      return this.contactId != null
    },
    uomTypeLookup() {
      return this.formatLookupName(this.uomType, UOMTypeUnitsLookup)
    }
  },
  mounted() {
    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    },
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.editingStartDate = this.startDate
      this.requestChartData()
    },
    endDate: function() {
      this.editingEndDate = this.endDate
      this.requestChartData()
    }
  },
  methods: {
    requestChartData() {
      var summaryFilter = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString(),
        contactId: this.contactId,
        stockId: this.stockId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
        maxGroupSize: 7
      }

      const widgetComp = this
      this.$apiService.analyticsService.getOrderItemsValueSummary(summaryFilter)
      .then(response => {
        this.orderValueOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${Utils.formatCurrency(param.value)} (${param.percent}%)`
          }
        }

        if(this.type == OrderItemValueChartTypes.orderItemValueContact) {
          this.orderValueSeries = [{
            name: 'Order Item Value',
            data: response.data.orderItemValueContactList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderItemValueContactList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Stock',
            valueTitle: 'Value',
            data: response.data.orderItemValueContactList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderItemValueChartTypes.orderItemUnitsContact) {
          this.orderValueSeries = [{
            name: 'Order Item Units',
            data: response.data.orderItemUnitsContactList.map(group => {
              return {
                name: group.name,
                value: Utils.roundNumber(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
           this.orderValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value, false, true)}${widgetComp.uomTypeLookup} (${param.percent}%)`
            }
          }
          this.orderValueLegend = {
            data: response.data.orderItemUnitsContactList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Stock',
            valueTitle: 'Units',
            data: response.data.orderItemUnitsContactList.map(group => {
              return {
                name: group.name,
                displayValue: `${Utils.formatInteger(group.value, false, true)}${widgetComp.uomTypeLookup}`
              }
            })
          }
        }

        this.orderValueLegend.show = this.showLegend
      })
    },
  }
}
</script>